import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, BehaviorSubject, combineLatest } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import {
  Contrato,
  CreateContratoRequest,
  UpdateContratoRequest
} from '../../models/contrato.interface';
import { CONTRATO_LABELS } from './contrato.labels';
import * as ContratoActions from '../../store/contrato.actions';
import * as ContratoSelectors from '../../store/contrato.selectors';
import * as TipoContratoSelectors from '../../store/tipo-contrato.selectors';
import * as TipoContratoActions from '../../store/tipo-contrato.actions';
import * as PerfilSelectors from '../../store/perfil.selectors';
import * as PerfilActions from '../../store/perfil.actions';
import * as RangoSalarialPerfilSelectors from '../../store/rango-salarial-perfil.selectors';
import * as RangoSalarialPerfilActions from '../../store/rango-salarial-perfil.actions';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { LoggerService } from '../../../../core/services/logger.service';
import { TrackByUtils } from '../../../../shared/utils/trackby.utils';

@Component({
  selector: 'app-contrato',
  templateUrl: './contrato.component.html',
  styleUrls: ['./contrato.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatPaginatorModule,
    RouterModule
  ]
})
export class ContratoComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly formBuilder = inject(FormBuilder);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly notificationService = inject(NotificationService);
  private readonly logger = inject(LoggerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly labels = CONTRATO_LABELS;

  // Observables del store
  contratos$ = this.store.select(ContratoSelectors.selectAllContratos);
  loading$ = this.store.select(ContratoSelectors.selectContratoLoading);
  error$ = this.store.select(ContratoSelectors.selectContratoError);
  creating$ = this.store.select(ContratoSelectors.selectContratoCreating);
  updating$ = this.store.select(ContratoSelectors.selectContratoUpdating);
  deleting$ = this.store.select(ContratoSelectors.selectContratoDeleting);

  // Observables para los selectores
  tiposContrato$ = this.store.select(TipoContratoSelectors.selectAllTiposContrato);
  perfiles$ = this.store.select(PerfilSelectors.selectAllPerfiles);
  rangosSalariales$ = this.store.select(RangoSalarialPerfilSelectors.selectAllRangosSalariales);

  // Filtros por columnas
  columnFilters: {
    numeroContrato: string;
    beneficiario: string;
    anioContrato: string;
    tipoContrato: string;
    perfil: string;
  } = {
    numeroContrato: '',
    beneficiario: '',
    anioContrato: '',
    tipoContrato: '',
    perfil: ''
  };

  // Subjects de filtrado reactivo
  private readonly searchSubject = new BehaviorSubject<string>('');
  private readonly columnFiltersSubject = new BehaviorSubject<typeof this.columnFilters>(
    this.columnFilters
  );
  private readonly filtersSubject = new BehaviorSubject<number>(0);

  // Data source para la tabla
  dataSource = new MatTableDataSource<Contrato>([]);
  displayedColumns: string[] = [
    'numeroContratoId',
    'numeroContrato',
    'anioContrato',
    'tipoContrato',
    'perfil',
    'valorHonorariosMes',
    'beneficiario',
    'actions'
  ];

  // Observable combinado para mostrar contratos aplicando búsqueda y filtros por columna
  displayContratos$ = combineLatest([
    this.contratos$,
    this.searchSubject.asObservable(),
    this.columnFiltersSubject.asObservable()
  ]).pipe(
    map(([contratos, searchQuery, columnFilters]) => {
      let result = contratos;

      // Búsqueda general
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        result = result.filter(
          c =>
            c.numeroContrato.toLowerCase().includes(query) ||
            c.beneficiario.toLowerCase().includes(query) ||
            (c.tipoContratoNombre || '').toLowerCase().includes(query) ||
            (c.perfilNombre || '').toLowerCase().includes(query) ||
            c.anioContrato.toString().includes(query)
        );
      }

      // Filtros por columna
      if (columnFilters.numeroContrato && columnFilters.numeroContrato.trim()) {
        const query = columnFilters.numeroContrato.toLowerCase().trim();
        result = result.filter(c => c.numeroContrato.toLowerCase().includes(query));
      }

      if (columnFilters.beneficiario && columnFilters.beneficiario.trim()) {
        const query = columnFilters.beneficiario.toLowerCase().trim();
        result = result.filter(c => c.beneficiario.toLowerCase().includes(query));
      }

      if (columnFilters.anioContrato && columnFilters.anioContrato.trim()) {
        const query = columnFilters.anioContrato.toLowerCase().trim();
        result = result.filter(c => c.anioContrato.toString().includes(query));
      }

      if (columnFilters.tipoContrato && columnFilters.tipoContrato.trim()) {
        const query = columnFilters.tipoContrato.toLowerCase().trim();
        result = result.filter(c => (c.tipoContratoNombre || '').toLowerCase().includes(query));
      }

      if (columnFilters.perfil && columnFilters.perfil.trim()) {
        const query = columnFilters.perfil.toLowerCase().trim();
        result = result.filter(c => (c.perfilNombre || '').toLowerCase().includes(query));
      }

      return result;
    })
  );

  // Propiedades para paginación
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];
  pageIndex = 0;

  // Propiedades para búsqueda
  searchQuery = '';
  showSearchPanel = false;

  // Formulario
  contratoForm: FormGroup;

  // Referencias a templates
  @ViewChild('createContratoModal') createContratoModal!: TemplateRef<unknown>;
  @ViewChild('paginator') set paginator(p: MatPaginator) {
    if (p) {
      this._paginator = p;
      this.dataSource.paginator = p;
    }
  }
  private _paginator!: MatPaginator;

  // Estado del componente
  selectedContrato: Contrato | null = null;
  isEditing = false;
  editingContrato: Contrato | null = null;

  // Estados para rastrear operaciones
  wasCreating = false;
  wasUpdating = false;

  // TrackBy functions
  trackByContratoId = TrackByUtils.trackByProperty<Contrato>('numeroContratoId');

  // Keep latest contratos for validators
  private currentContratos: Contrato[] = [];
  private currentRangosSalariales: Array<{
    rangoSalarialId: number;
    perfilId: number;
    ano: number;
    minimo: number;
    maximo: number;
  }> = [];

  // Selected perfil range info
  selectedPerfilRange: { minimo: number; maximo: number } | null = null;

  constructor() {
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear + 10;

    this.contratoForm = this.formBuilder.group(
      {
        numeroContrato: ['', [Validators.required, Validators.maxLength(50)]],
        anioContrato: [
          currentYear,
          [Validators.required, Validators.min(currentYear), Validators.max(maxYear)]
        ],
        tipoContratoId: ['', [Validators.required]],
        perfilId: ['', [Validators.required]],
        valorHonorariosMes: ['', [Validators.required, Validators.min(0.01)]],
        beneficiario: ['', [Validators.required, Validators.maxLength(200)]]
      },
      {
        validators: [
          this.duplicateContratoValidator.bind(this),
          this.rangoSalarialValidator.bind(this)
        ]
      }
    );

    // Listen to perfil and año changes to update salary range info
    this.contratoForm.get('perfilId')?.valueChanges.subscribe(() => {
      this.updateSelectedPerfilRange();
    });

    this.contratoForm.get('anioContrato')?.valueChanges.subscribe(() => {
      this.updateSelectedPerfilRange();
    });
  }

  ngOnInit(): void {
    this.loadTiposContrato();
    this.loadPerfiles();
    this.loadRangosSalariales();
    this.loadContratos();
    this.setupSubscriptions();
  }

  ngAfterViewInit(): void {
    // Connect paginator to data source immediately
    this.dataSource.paginator = this._paginator;

    // Ensure paginator is configured correctly
    if (this._paginator) {
      this._paginator.pageSize = this.pageSize;
      this._paginator.pageSizeOptions = this.pageSizeOptions;
    }

    // Initialize filter subjects
    this.searchSubject.next('');
    this.columnFiltersSubject.next({
      numeroContrato: '',
      beneficiario: '',
      anioContrato: '',
      tipoContrato: '',
      perfil: ''
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSubscriptions(): void {
    // Subscribe to filtered contratos to update data source
    this.displayContratos$.pipe(takeUntil(this.destroy$)).subscribe(contratos => {
      // Update the existing data source
      this.dataSource.data = contratos;

      // Ensure paginator is connected
      if (this._paginator && !this.dataSource.paginator) {
        this.dataSource.paginator = this._paginator;
      }
    });

    // Subscribe to all contratos for validations
    this.contratos$.pipe(takeUntil(this.destroy$)).subscribe(contratos => {
      this.currentContratos = contratos;
      // Revalidate form when data changes
      this.contratoForm.updateValueAndValidity({ onlySelf: true });
    });

    // Subscribe to rangos salariales for validations
    this.rangosSalariales$.pipe(takeUntil(this.destroy$)).subscribe(rangos => {
      this.currentRangosSalariales = rangos;
      this.updateSelectedPerfilRange();
      // Revalidate form when rangos change
      this.contratoForm.updateValueAndValidity({ onlySelf: true });
    });

    // Subscribe to errors
    this.error$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error) {
        this.errorHandler.handleError(error);
        this.store.dispatch(ContratoActions.clearContratoError());
      }
    });

    // Subscribe to creation success
    this.creating$.pipe(takeUntil(this.destroy$)).subscribe(creating => {
      if (!creating && this.wasCreating) {
        this.notificationService.showSuccessModal('Contrato creado exitosamente');
      }
      this.wasCreating = creating;
    });

    // Subscribe to update success
    this.updating$.pipe(takeUntil(this.destroy$)).subscribe(updating => {
      if (!updating && this.wasUpdating) {
        this.notificationService.showSuccessModal('Contrato actualizado exitosamente');
      }
      this.wasUpdating = updating;
    });
  }

  private loadTiposContrato(): void {
    this.store.dispatch(TipoContratoActions.loadTiposContrato({ force: false }));
  }

  private loadPerfiles(): void {
    this.store.dispatch(PerfilActions.loadPerfiles());
  }

  private loadRangosSalariales(): void {
    this.store.dispatch(RangoSalarialPerfilActions.loadRangosSalariales({ force: false }));
  }

  private loadContratos(): void {
    this.store.dispatch(ContratoActions.loadContratos());
  }

  private updateSelectedPerfilRange(): void {
    const perfilId = this.contratoForm.get('perfilId')?.value;
    const anio = this.contratoForm.get('anioContrato')?.value;

    if (perfilId && anio && this.currentRangosSalariales.length > 0) {
      const rango = this.currentRangosSalariales.find(
        r => r.perfilId === perfilId && r.ano === anio
      );

      if (rango) {
        this.selectedPerfilRange = {
          minimo: rango.minimo,
          maximo: rango.maximo
        };
      } else {
        this.selectedPerfilRange = null;
      }
    } else {
      this.selectedPerfilRange = null;
    }

    this.cdr.markForCheck();
  }

  // Métodos de filtrado
  toggleSearch(): void {
    this.showSearchPanel = !this.showSearchPanel;
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.searchSubject.next(this.searchQuery);
    if (this._paginator) {
      this._paginator.firstPage();
    }
  }

  onColumnFilterChange(column: keyof typeof this.columnFilters, value: string): void {
    this.columnFilters[column] = value;
    this.columnFiltersSubject.next({ ...this.columnFilters });
    this.filtersSubject.next(Date.now());
    if (this._paginator) {
      this._paginator.firstPage();
    }
  }

  clearFilters(): void {
    // Check if there are any active filters
    const hasActiveFilters =
      this.searchQuery !== '' ||
      this.columnFilters.numeroContrato !== '' ||
      this.columnFilters.beneficiario !== '' ||
      this.columnFilters.anioContrato !== '' ||
      this.columnFilters.tipoContrato !== '' ||
      this.columnFilters.perfil !== '';

    this.searchQuery = '';
    this.columnFilters = {
      numeroContrato: '',
      beneficiario: '',
      anioContrato: '',
      tipoContrato: '',
      perfil: ''
    };
    this.searchSubject.next('');
    this.columnFiltersSubject.next({ ...this.columnFilters });
    this.filtersSubject.next(Date.now());

    // Only go to first page if there were active filters
    if (hasActiveFilters && this._paginator) {
      this._paginator.firstPage();
    }
  }

  // Métodos de modal
  openCreateModal(contrato?: Contrato): void {
    this.isEditing = !!contrato;
    this.editingContrato = contrato || null;
    this.selectedContrato = contrato || null;

    if (this.isEditing && contrato) {
      // In edit mode, allow past years (set min to 2000)
      const maxYear = new Date().getFullYear() + 10;
      this.contratoForm
        .get('anioContrato')
        ?.setValidators([Validators.required, Validators.min(2000), Validators.max(maxYear)]);
      this.contratoForm.get('anioContrato')?.updateValueAndValidity();

      this.contratoForm.patchValue({
        numeroContrato: contrato.numeroContrato,
        anioContrato: contrato.anioContrato,
        tipoContratoId: contrato.tipoContratoId,
        perfilId: contrato.perfilId,
        valorHonorariosMes: contrato.valorHonorariosMes,
        beneficiario: contrato.beneficiario
      });

      // Make numeroContrato and anioContrato read-only in edit mode
      this.contratoForm.get('numeroContrato')?.disable();
      this.contratoForm.get('anioContrato')?.disable();
    } else {
      // In create mode, require current year or future
      const currentYear = new Date().getFullYear();
      const maxYear = currentYear + 10;
      this.contratoForm
        .get('anioContrato')
        ?.setValidators([
          Validators.required,
          Validators.min(currentYear),
          Validators.max(maxYear)
        ]);
      this.contratoForm.get('anioContrato')?.updateValueAndValidity();

      this.contratoForm.reset({
        anioContrato: currentYear
      });

      // Enable all fields in create mode
      this.contratoForm.get('numeroContrato')?.enable();
      this.contratoForm.get('anioContrato')?.enable();
    }

    // Update perfil range info
    this.updateSelectedPerfilRange();

    this.dialog.open(this.createContratoModal, {
      width: '800px',
      maxWidth: '90vw',
      disableClose: false
    });
  }

  closeModal(): void {
    this.dialog.closeAll();
    this.resetForm();
  }

  resetForm(): void {
    this.contratoForm.reset();
    this.contratoForm.get('numeroContrato')?.enable();
    this.contratoForm.get('anioContrato')?.enable();
    this.isEditing = false;
    this.editingContrato = null;
    this.selectedContrato = null;
  }

  // Métodos de formulario
  async saveContrato(): Promise<void> {
    if (
      this.contratoForm.valid ||
      (this.isEditing && this.contratoForm.get('numeroContrato')?.disabled)
    ) {
      // Get raw value to include disabled fields
      const formData = this.contratoForm.getRawValue();

      if (this.isEditing && this.editingContrato) {
        // Show confirmation first for update
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea actualizar este contrato?',
          'Confirmar Actualización'
        );

        if (confirmed) {
          const updateRequest: UpdateContratoRequest = {
            numeroContrato: formData.numeroContrato,
            anioContrato: formData.anioContrato,
            tipoContratoId: formData.tipoContratoId,
            perfilId: formData.perfilId,
            valorHonorariosMes: formData.valorHonorariosMes,
            beneficiario: formData.beneficiario
          };

          this.store.dispatch(
            ContratoActions.updateContrato({
              id: this.editingContrato.numeroContratoId,
              contrato: updateRequest
            })
          );

          this.closeModal();
        }
      } else {
        // Show confirmation first for create
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea crear este contrato?',
          'Confirmar Creación'
        );

        if (confirmed) {
          const createRequest: CreateContratoRequest = {
            numeroContrato: formData.numeroContrato,
            anioContrato: formData.anioContrato,
            tipoContratoId: formData.tipoContratoId,
            perfilId: formData.perfilId,
            valorHonorariosMes: formData.valorHonorariosMes,
            beneficiario: formData.beneficiario
          };

          this.store.dispatch(ContratoActions.createContrato({ contrato: createRequest }));

          this.closeModal();
        }
      }
    } else {
      this.notificationService.showErrorModal(
        'Error de validación',
        'Por favor complete todos los campos requeridos'
      );
    }
  }

  // Métodos de acciones
  selectContrato(contrato: Contrato): void {
    this.selectedContrato = contrato;
    this.store.dispatch(ContratoActions.selectContrato({ contrato }));
  }

  // Validadores personalizados
  private duplicateContratoValidator(form: FormGroup) {
    const numeroContrato = form.get('numeroContrato')?.value;
    const anioContrato = form.get('anioContrato')?.value;

    if (!numeroContrato || !anioContrato) {
      return null;
    }

    // Check if exists in current contratos (excluding the one being edited)
    const duplicate = this.currentContratos.find(
      c =>
        c.numeroContrato === numeroContrato &&
        c.anioContrato === anioContrato &&
        (!this.isEditing || c.numeroContratoId !== this.editingContrato?.numeroContratoId)
    );

    return duplicate ? { duplicateContrato: { numeroContrato, anioContrato } } : null;
  }

  private rangoSalarialValidator(form: FormGroup) {
    const perfilId = form.get('perfilId')?.value;
    const anioContrato = form.get('anioContrato')?.value;
    const valorHonorarios = form.get('valorHonorariosMes')?.value;

    if (!perfilId || !anioContrato || !valorHonorarios) {
      return null;
    }

    // Find salary range for selected profile and year
    const rango = this.currentRangosSalariales.find(
      r => r.perfilId === perfilId && r.ano === anioContrato
    );

    if (!rango) {
      // No range defined for this profile and year - show warning but don't block
      return { noRangoSalarial: true };
    }

    // Validate if valor is within range
    if (valorHonorarios < rango.minimo || valorHonorarios > rango.maximo) {
      return {
        fueraDeRango: {
          minimo: rango.minimo,
          maximo: rango.maximo,
          valor: valorHonorarios
        }
      };
    }

    return null;
  }

  // Métodos de validación
  hasError(controlName: string): boolean {
    const control = this.contratoForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getErrorMessage(controlName: string): string {
    const control = this.contratoForm.get(controlName);
    if (control && control.errors) {
      if (control.errors['required']) {
        return 'Este campo es requerido';
      }
      if (control.errors['maxlength']) {
        const maxLength = control.errors['maxlength'].requiredLength;
        return `Máximo ${maxLength} caracteres`;
      }
      if (control.errors['min']) {
        const minValue = control.errors['min'].min;
        switch (controlName) {
          case 'anioContrato': {
            const currentYear = new Date().getFullYear();
            return `No se permiten contratos con años anteriores al actual (${currentYear})`;
          }
          case 'valorHonorariosMes': {
            return 'El valor de los honorarios debe ser mayor a cero';
          }
        }
        return `El valor mínimo es ${minValue}`;
      }
      if (control.errors['max']) {
        const maxValue = control.errors['max'].max;
        if (controlName === 'anioContrato') {
          return `El año máximo es ${maxValue}`;
        }
        return `El valor máximo es ${maxValue}`;
      }
    }
    return '';
  }

  getFormError(): string {
    const errors = this.contratoForm.errors;

    if (errors?.['duplicateContrato']) {
      const { numeroContrato, anioContrato } = errors['duplicateContrato'];
      return `Ya existe un contrato con el número ${numeroContrato} para el año ${anioContrato}`;
    }

    if (errors?.['noRangoSalarial']) {
      return 'Advertencia: No hay rango salarial definido para este perfil y año';
    }

    if (errors?.['fueraDeRango']) {
      const { minimo, maximo, valor } = errors['fueraDeRango'];
      return `El valor de honorarios ($${this.formatNumber(valor)}) debe estar entre $${this.formatNumber(minimo)} y $${this.formatNumber(maximo)} según el rango salarial del perfil`;
    }

    return '';
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('es-CO').format(value);
  }

  // Métodos de paginación
  onPageChange(): void {
    // Pagination is handled automatically by MatPaginator
  }

  // Métodos de utilidad
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  }
}
